import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

interface BackButtonContextValue {
  hasBack: boolean;
  acquire: () => () => void;
}

const BackButtonContext = createContext<BackButtonContextValue | null>(null);

export function BackButtonProvider({ children }: { children: ReactNode }) {
  const [count, setCount] = useState(0);

  const acquire = useCallback(() => {
    setCount((c) => c + 1);
    return () => setCount((c) => Math.max(0, c - 1));
  }, []);

  const value = useMemo<BackButtonContextValue>(
    () => ({
      hasBack: count > 0,
      acquire,
    }),
    [count, acquire],
  );

  return (
    <BackButtonContext.Provider value={value}>
      {children}
    </BackButtonContext.Provider>
  );
}

export function useHasBackButton(): boolean {
  return useContext(BackButtonContext)?.hasBack ?? false;
}

/** Mount-only hook: registers presence of a back affordance on this route. */
export function useReportBackButton(active: boolean) {
  const acquire = useContext(BackButtonContext)?.acquire;
  useEffect(() => {
    if (!acquire || !active) return;
    return acquire();
  }, [acquire, active]);
}
